import { create } from 'zustand';
import { Capacitor } from '@capacitor/core';
import { api } from '../services/api';
import { scheduleProactiveRefresh, cancelProactiveRefresh } from '../services/api';
import { wsClient } from '../services/websocket';
import NoticeBoardWidget from '../plugins/NoticeBoardWidgetPlugin';

const syncAuthToWidget = (token: string, refreshToken: string) => {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    NoticeBoardWidget.storeAuthToken({
      token,
      refreshToken,
      apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001',
    }).catch(() => {});
  }
};

interface User {
  id: string;
  email: string;
  displayName: string;
  colour: string;
  avatar?: string;
  preferences?: Record<string, any>;
}

interface Family {
  id: string;
  name: string;
  inviteCode?: string;
  role?: string;
  address_line?: string | null;
  postcode?: string | null;
  colour?: string | null;
  members?: FamilyMember[];
}

interface FamilyMember {
  id: string;
  displayName: string;
  colour: string;
  avatar?: string;
  role: string;
  age?: number | null;
}

interface AuthState {
  user: User | null;
  family: Family | null;
  accessToken: string | null;
  refreshToken: string | null;
  fcmToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  initAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  kidLogin: (name: string, familyCode: string, passcode: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setFcmToken: (token: string) => void;
  setFamily: (family: Family | null) => void;
  refreshFamily: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  family: null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  fcmToken: null,
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isLoading: !!localStorage.getItem('accessToken'),

  initAuth: async () => {
    const { accessToken, refreshToken } = get();
    if (!accessToken) {
      set({ isLoading: false });
      return;
    }

    set({ isLoading: true });

    try {
      const res = await api.get('/auth/me');
      set({
        user: res.data.user,
        family: res.data.family,
        isAuthenticated: true,
        isLoading: false,
      });

      // Fetch full family data (including members) if user belongs to a family
      if (res.data.family) {
        await get().refreshFamily();
      }

      if (accessToken) {
        wsClient.connect(accessToken);
        scheduleProactiveRefresh();
      }
    } catch {
      // Token might be expired, try to refresh
      if (refreshToken) {
        try {
          const refreshRes = await api.post('/auth/refresh', { refreshToken });
          const { accessToken: newToken, refreshToken: newRefresh } = refreshRes.data;
          localStorage.setItem('accessToken', newToken);
          localStorage.setItem('refreshToken', newRefresh);
          set({ accessToken: newToken, refreshToken: newRefresh });

          const meRes = await api.get('/auth/me');
          set({
            user: meRes.data.user,
            family: meRes.data.family,
            isAuthenticated: true,
            isLoading: false,
          });
          if (meRes.data.family) {
            await get().refreshFamily();
          }
          wsClient.connect(newToken);
          scheduleProactiveRefresh();
        } catch {
          get().logout();
        }
      } else {
        get().logout();
      }
    }
  },

  login: async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { user, family, accessToken, refreshToken } = res.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    set({ user, family, accessToken, refreshToken, isAuthenticated: true });
    syncAuthToWidget(accessToken, refreshToken);
    // Fetch full family data (including members) if user belongs to a family
    if (family) {
      await get().refreshFamily();
    }
    wsClient.connect(accessToken);
    scheduleProactiveRefresh();
  },

  kidLogin: async (name: string, familyCode: string, passcode: string) => {
    const res = await api.post('/auth/kid-login', { name, familyCode, passcode });
    const { user, family, accessToken, refreshToken } = res.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    set({ user, family, accessToken, refreshToken, isAuthenticated: true });
    syncAuthToWidget(accessToken, refreshToken);
    if (family) {
      await get().refreshFamily();
    }
    wsClient.connect(accessToken);
    scheduleProactiveRefresh();
  },

  register: async (email, password, displayName) => {
    const res = await api.post('/auth/register', { email, password, displayName });
    const { user, accessToken, refreshToken } = res.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    set({ user, family: null, accessToken, refreshToken, isAuthenticated: true });
    syncAuthToWidget(accessToken, refreshToken);
    wsClient.connect(accessToken);
    scheduleProactiveRefresh();
  },

  logout: () => {
    const { refreshToken } = get();
    if (refreshToken) {
      api.post('/auth/logout', { refreshToken }).catch(() => {});
    }
    cancelProactiveRefresh();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('fh-active-profile');
    wsClient.disconnect();
    set({
      user: null,
      family: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    set({ accessToken, refreshToken });
    syncAuthToWidget(accessToken, refreshToken);
    // Keep WebSocket connection alive with fresh token
    wsClient.updateToken(accessToken);
  },

  setFcmToken: (token) => set({ fcmToken: token }),

  setFamily: (family) => set({ family }),

  refreshFamily: async () => {
    try {
      const res = await api.get('/family/me');
      set({ family: res.data.family });
    } catch (err) {
      console.error('Failed to refresh family', err);
    }
  },
}));
